import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "../context/TranslationContext";
import { useWebSocket } from '../context/WebSocketContext';
import { useNotification } from '../context/NotificationContext';
import { ConnectionEventEmitter } from '../utils/ConnectionEventEmitter';
import PropTypes from "prop-types";
import '../styles/List.css';
import config from '../config'; 
import Button from './Button';
import Input from './Input';
import PanelHorizontal from './PanelHorizontal';
import PanelVertical from './PanelVertical';
import PreviewIcon from './PreviewIcon';

import { ReactComponent as IconLogoSymbol } from "../resources/assets/logoSymbol.svg";
import { ReactComponent as IconSort } from "../resources/assets/iconSort.svg";

const List = ({ title, screens, onBack, onAdd, onClick, getLabel, getDescription }) => {
  const { t, language } = useTranslation();
  const { sendMessage } = useWebSocket();
  const { showNotification } = useNotification();
  const [list, setList] = useState();
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState(() => localStorage.getItem(screens.module+"_sortField") || "name");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem(screens.module+"_sortOrder") || "ascending");
  const [search, setSearch] = useState(() => localStorage.getItem(screens.module+"_search") || "");

  useEffect(() => {
    localStorage.setItem(screens.module+"_sortField", sortField);
  }, [sortField]);

  useEffect(() => {
    localStorage.setItem(screens.module+"_sortOrder", sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem(screens.module+"_search", search);
  }, [search]);

  useEffect(() => {    
    ConnectionEventEmitter.on('received', connectionReceived);

    setLoading(true);
    sendMessage({
      area: "list",
      command: "list",
      module: screens.module
    });

    return () => { 
      ConnectionEventEmitter.off('received', connectionReceived);
    }
  }, []);
  
  const connectionReceived = useCallback(async (receivedMessage) => {
    if(receivedMessage) {
      if(receivedMessage.area==="list") {
        if(receivedMessage.command==="list") {
          setList(receivedMessage.list);
          setLoading(false);
        } else
        if(receivedMessage.command==="get") {
          if(receivedMessage.status==="ok") {
            onClick(receivedMessage.item);
          } else {
            showNotification(t.host.ListCantLoadItem);
          }
          setLoading(false);
        }
      }
    }
  });

  const handleClick = (item) => {
    setLoading(true);
    sendMessage({
      area: "list",
      command: "get",
      itemId: item.id,
      module: screens.module
    });
  };

  const filteredList = list ? list.filter((item) => {
    var label = getLabel(item,language);
    if(label) label = label.toLowerCase();
    else label = "";

    var description = getDescription(item,language);
    if(description) description = description.toLowerCase();
    else description = "";

    var state = t.state[item.state];
    if(state) state = state.toLowerCase();
    else state = "";

    return label.includes(search.toLowerCase()) || description.includes(search.toLowerCase()) || state.includes(search.toLowerCase());
  }) : null;

  const sortedList = filteredList ? [...filteredList].sort((a, b) => {
    const valueA = sortField === "name" ? getLabel(a,language).toLowerCase() : getDescription(a,language).toLowerCase();
    const valueB = sortField === "name" ? getLabel(b,language).toLowerCase() : getDescription(b,language).toLowerCase();
    
    return sortOrder === "ascending" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
  }) : null;

  const generateElements = () => {
    return sortedList.map((item) => (
      <PreviewIcon
        key={item.id}
        onClick={() => handleClick(item)}
        image={config.photos[screens.module]+screens.module+"/"+item.id+"/icon_"+item.images[0]+".jpg?v=" +item.timestamp}
        label={getLabel(item,language)}   
        slogan={getDescription(item,language)}
        state={item.state}
        showEnter={true} 
        showBorder={true}
      />
    ));
  };

  const generateList = () => {
    if(loading) {
      return (
        <>
          <div className="list-end-border"></div>
          <div className="list-loading-back">
            <div className="list-loading-dots">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </>
      );
    }
    if(sortedList&&sortedList.length>0) {
      return (
        <>
          {generateElements()}
          <div className="list-end-border"></div>
        </>
      );
    } else {
      return (
        <>
          <div className="list-end-border"></div>
          <div className="list-loading-back">{t.host.ListEmpty}</div>
        </>
      );
    }
  };

  const toggleSort = () => {
    if(sortField==="name") {
      if(sortOrder==="ascending") {
        setSortOrder("descending");
      } else {
        setSortOrder("ascending");
        setSortField("descending");
      }
    } else {
      if(sortOrder==="ascending") {
        setSortOrder("descending");
      } else {
        setSortOrder("ascending");
        setSortField("name");
      }
    }
  };

  const handleInputChange = (value) => {
    setSearch(value);
  };

  return (
    <div className="list-container">
      <PanelVertical height="var(--list-height)">
        <PanelHorizontal>
          <IconLogoSymbol />
          <div className="list-header">{title}</div>
          <IconSort 
            onClick={toggleSort} 
            style={{
              cursor: 'pointer',
              height: "var(--list-height)",
              transform: sortOrder === 'descending' ? 'scale(-1)' : 'none'
            }}
          />
        </PanelHorizontal>
      </PanelVertical>

      <div className="list-center">
        <div className="list-content">
          {generateList()}
        </div>
      </div>

      <div className="list-bottom">
        <div className="list-bottom-first">
          <Input placeholder={t.general.Search} type="search" value={search} minLength="5" maxLength="128" onChange={(value) => handleInputChange(value)} /> 
        </div>
        <div className="list-bottom-second" height="var(--button-height)">
          <Button type="normal" onClick={onBack}>{t.general.Back}</Button>
          <Button type="priority" onClick={onAdd}>{t.general.Add}</Button>
        </div>
      </div>
    </div>
  );
};

List.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  onBack: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default List;